import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllProjects, getAllReports } from '../../Services/EditableReportService';
import { AdminProject, EditableReport } from '../../Types/EditableReport.types';

const AllProjectsAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [reports, setReports] = useState<EditableReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Per-column search filters.
  const [projectSearch, setProjectSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [architectSearch, setArchitectSearch] = useState('');
  const [monthFilter, setMonthFilter] = useState(''); // "YYYY-MM"

  // Display name for an architect: full name when available, otherwise username (email).
  const architectName = (owner: AdminProject['owner']): string => {
    const full = `${owner.firstName ?? ''} ${owner.lastName ?? ''}`.trim();
    return full || owner.username;
  };

  // Client full name (empty when the client hasn't registered yet — only email is shown).
  const clientName = (project: AdminProject): string => {
    return `${project.client?.firstName ?? ''} ${project.client?.lastName ?? ''}`.trim();
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [projectsData, reportsData] = await Promise.all([
        getAllProjects(),
        getAllReports(),
      ]);
      setProjects(projectsData);
      setReports(reportsData);
    } catch (err: any) {
      setError(err.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter((p) => {
    const projectQ = projectSearch.toLowerCase().trim();
    const clientQ = clientSearch.toLowerCase().trim();
    const architectQ = architectSearch.toLowerCase().trim();

    const matchesProject = !projectQ
      || p.externalName.toLowerCase().includes(projectQ)
      || p.internalName.toLowerCase().includes(projectQ);

    const matchesClient = !clientQ
      || clientName(p).toLowerCase().includes(clientQ)
      || p.clientEmail.toLowerCase().includes(clientQ);

    const matchesArchitect = !architectQ
      || architectName(p.owner).toLowerCase().includes(architectQ)
      || p.owner.username.toLowerCase().includes(architectQ);

    // createdAt is ISO 8601 ("YYYY-MM-DD..."), monthFilter is "YYYY-MM".
    const matchesMonth = !monthFilter || p.createdAt.slice(0, 7) === monthFilter;

    return matchesProject && matchesClient && matchesArchitect && matchesMonth;
  });

  const hasActiveFilters = !!(projectSearch || clientSearch || architectSearch || monthFilter);

  const clearFilters = () => {
    setProjectSearch('');
    setClientSearch('');
    setArchitectSearch('');
    setMonthFilter('');
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <i className="bi bi-folder2-open me-2"></i>
          Todos los Proyectos
        </h2>
        {hasActiveFilters && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={clearFilters}
          >
            <i className="bi bi-x-circle me-1"></i>
            Borrar filtros
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Proyecto</th>
                  <th>Cliente</th>
                  <th>Arquitecto</th>
                  <th>Fecha</th>
                  <th style={{ width: '40px' }}></th>
                </tr>
                <tr>
                  <th className="pt-2 pb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Buscar proyecto..."
                      value={projectSearch}
                      onChange={(e) => setProjectSearch(e.target.value)}
                    />
                  </th>
                  <th className="pt-2 pb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Buscar cliente..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                    />
                  </th>
                  <th className="pt-2 pb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Buscar arquitecto..."
                      value={architectSearch}
                      onChange={(e) => setArchitectSearch(e.target.value)}
                    />
                  </th>
                  <th className="pt-2 pb-2">
                    <input
                      type="month"
                      className="form-control form-control-sm"
                      value={monthFilter}
                      onChange={(e) => setMonthFilter(e.target.value)}
                      title="Filtrar por año y mes"
                    />
                  </th>
                  <th className="pt-2 pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">
                      No hay proyectos disponibles
                    </td>
                  </tr>
                ) : (
                  filteredProjects.map((project) => {
                    const cName = clientName(project);
                    return (
                      <tr
                        key={project.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/projects/${project.id}`)}
                      >
                        <td>
                          <strong>{project.externalName}</strong>
                          <br />
                          <small className="text-muted">{project.internalName}</small>
                        </td>
                        <td>
                          {cName ? (
                            <div>{cName}</div>
                          ) : (
                            <span
                              className="badge bg-warning-subtle text-warning-emphasis"
                              title="El cliente todavía no ha aceptado la invitación / creado su cuenta"
                            >
                              <i className="bi bi-person-x me-1"></i>
                              Usuario no registrado
                            </span>
                          )}
                          <small className="text-muted d-block">{project.clientEmail}</small>
                        </td>
                        <td>{architectName(project.owner)}</td>
                        <td>
                          <small>{new Date(project.createdAt).toLocaleDateString('es-ES')}</small>
                        </td>
                        <td className="text-center">
                          <i className="bi bi-chevron-right text-muted"></i>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-3 text-muted">
        <small>
          <i className="bi bi-info-circle me-1"></i>
          Total: {projects.length} proyectos | {reports.length} informes generados
        </small>
      </div>
    </div>
  );
};

export default AllProjectsAdmin;
